import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Select, SelectItem } from '@nextui-org/react';
import { CtyButton, CtySpinner, EMAIL_CONSTANTS } from '@circuitry-ai/doc-ui';
import { 
  useGetFgaGroupsQuery,
  useAddFgaUserMutation,
  useRemoveFgaUserMutation,
  useFgaInviteMutation,
  useUpdateUserMetadataMutation,
} from '../../../../shared/data/src/features/settings/settingsApiSlice';
import { handleApiError, handleApiSuccess } from '../fga-utils';
import { UserAttributesEditor, UserAttribute, userMetadataToAttributes, attributesToUserMetadata, getMetadataDiff } from './UserAttributesEditor';

const ORG_METADATA_KEYS = ['organization_type', 'organization_code', 'organization_name'] as const;
const ORGANIZATION_TYPE_OPTIONS = ['Dealer', 'Supplier', 'Customer', 'Service center'] as const;
const EMPTY_FORM = { firstName: '', lastName: '', email: '', organizationType: '', organizationCode: '', organizationName: '' };

const stripOrgMetadata = (metadata: Record<string, string>): Record<string, string> => {
  const rest = { ...metadata };
  for (const key of ORG_METADATA_KEYS) {
    delete rest[key];
  }
  return rest;
};

export interface CreateEditUserModalProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly mode: 'create' | 'edit';
  readonly userData?: {
    user_id: string;
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    groups?: string[];
    user_metadata?: Record<string, string>;
  };
  readonly isUserDataLoading?: boolean;
  readonly onSuccess?: () => void;
}

export function CreateEditUserModal({ 
  isOpen, 
  onOpenChange, 
  mode, 
  userData,
  isUserDataLoading,
  onSuccess
}: CreateEditUserModalProps) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isGroupsExpanded, setIsGroupsExpanded] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [userAttributes, setUserAttributes] = useState<UserAttribute[]>([]);
  const [originalMetadata, setOriginalMetadata] = useState<Record<string, string>>({});

  // API hooks
  const { data: groupsData, isFetching: groupsFetching, refetch: refetchGroups } = useGetFgaGroupsQuery();
  const [addFgaUser] = useAddFgaUserMutation();
  const [removeFgaUser] = useRemoveFgaUserMutation();
  const [fgaInvite] = useFgaInviteMutation();
  const [updateUserMetadata] = useUpdateUserMetadataMutation();

  const allGroups = groupsData?.data?.groups || [];

  const filteredGroups = useMemo(() => 
    groupSearchTerm.trim() ? 
    allGroups.filter((group: any) => group.group.toLowerCase().includes(groupSearchTerm.toLowerCase())) : 
    allGroups,
    [allGroups, groupSearchTerm]
  );

  const organizationTypeOptions = useMemo(() => {
    if (!formData.organizationType || ORGANIZATION_TYPE_OPTIONS.includes(formData.organizationType as typeof ORGANIZATION_TYPE_OPTIONS[number])) {
      return ORGANIZATION_TYPE_OPTIONS;
    }
    return [formData.organizationType, ...ORGANIZATION_TYPE_OPTIONS];
  }, [formData.organizationType]);

  useEffect(() => {
    if (isOpen) {
      refetchGroups();
    } else {
      setFormData(EMPTY_FORM);
      setSelectedGroups([]);
      setIsGroupsExpanded(false);
      setGroupSearchTerm('');
      setEmailError('');
      setUserAttributes([]);
      setOriginalMetadata({});
    }
  }, [isOpen, refetchGroups]);

  useEffect(() => {
    if (isOpen && mode === 'edit' && userData) {
      const meta = userData.user_metadata ?? {};
      setFormData({
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email,
        organizationType: meta.organization_type || '',
        organizationCode: meta.organization_code || '',
        organizationName: meta.organization_name || '',
      });
      setSelectedGroups(userData.groups || []);
      setEmailError('');
      setOriginalMetadata(meta);
      setUserAttributes(userMetadataToAttributes(stripOrgMetadata(meta)));
    }
  }, [isOpen, mode, userData]);

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleRemoveGroup = (groupId: string) => {
    setSelectedGroups(prev => prev.filter(id => id !== groupId));
  };

  const handleSubmit = async () => {
    if (mode === 'create') {
      if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) return;
      
      // Validate email format
      if (!EMAIL_CONSTANTS.VALIDATION_REGEX.test(formData.email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
    }
    
    setIsLoading(true);
    try {
      const orgAttributes: UserAttribute[] = [
        { key: 'organization_type', value: formData.organizationType.trim() },
        { key: 'organization_code', value: formData.organizationCode.trim() },
        { key: 'organization_name', value: formData.organizationName.trim() },
      ];
      const combinedAttributes = [
        ...orgAttributes.filter((attr) => attr.value),
        ...userAttributes.filter((attr) => !ORG_METADATA_KEYS.includes(attr.key as typeof ORG_METADATA_KEYS[number])),
      ];

      if (mode === 'create') {
        const metadata = attributesToUserMetadata(combinedAttributes);
        const response = await fgaInvite({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          mail_id: formData.email.trim(),
          groups: selectedGroups,
          ...(Object.keys(metadata).length > 0 && { user_metadata: metadata }),
        }).unwrap();
        const message = (response as any)?.data?.message ?? (response as any)?.message ?? 'User created successfully';
        handleApiSuccess(message);
        onSuccess?.();
        onOpenChange(false);
        return;
      }

      const userId = userData!.user_id;
      const currentGroups = userData?.groups || [];
      
      // Remove from groups no longer selected
      for (const groupId of currentGroups) {
        if (!selectedGroups.includes(groupId)) {
          await removeFgaUser({ user_id: userId, group: groupId }).unwrap();
        }
      }
      
      // Add to newly selected groups
      for (const groupId of selectedGroups) {
        if (!currentGroups.includes(groupId)) {
          await addFgaUser({ user_id: userId, group: groupId }).unwrap();
        }
      }

      const metadataDiff = getMetadataDiff(originalMetadata, combinedAttributes);
      if (Object.keys(metadataDiff).length > 0) {
        await updateUserMetadata({ user_id: userId, user_metadata: metadataDiff }).unwrap();
      }
      
      handleApiSuccess('User updated successfully');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, mode === 'create' ? 'create user' : 'update user');
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = mode === 'edit' 
    ? selectedGroups.length > 0
    : formData.firstName.trim() && formData.lastName.trim() && formData.email.trim() && EMAIL_CONSTANTS.VALIDATION_REGEX.test(formData.email) && selectedGroups.length > 0;

  if (!isOpen) return null;

  return (
    <>
      <button 
        className="fixed inset-0 bg-black bg-opacity-50 z-[42]" 
        onClick={() => onOpenChange(false)} 
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-content1 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-default-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {mode === 'create' ? 'Create New User' : 'Edit User'}
                </h2>
                <p className="text-sm text-default-500 mt-1">
                  {mode === 'create' 
                    ? 'Create a new user and assign them to roles. If the user already exists, their roles will be updated.'
                    : 'Update the user details and role assignments.'
                  }
                </p>
              </div>
              <button 
                onClick={() => onOpenChange(false)}
                className="p-2 hover:bg-default-100 rounded-full"
              >
                <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
              </button>
            </div>
          </div>

          {(groupsFetching || isUserDataLoading) ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <CtySpinner size="md" />
            </div>
          ) : (
          <>
          {/* Form Content */}
          <div className="p-6 space-y-6">
            {/* User Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-default-600 mb-1">
                    First Name *
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Enter first name"
                    readOnly={mode === 'edit'}
                    className={`w-full px-3 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${mode === 'edit' ? 'bg-default-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-default-600 mb-1">
                    Last Name *
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Enter last name"
                    readOnly={mode === 'edit'}
                    className={`w-full px-3 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${mode === 'edit' ? 'bg-default-100 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-default-600 mb-1">
                  Email Address *
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, email: e.target.value }));
                    setEmailError('');
                  }}
                  onBlur={() => {
                    if (formData.email.trim() && !EMAIL_CONSTANTS.VALIDATION_REGEX.test(formData.email)) {
                      setEmailError('Please enter a valid email address');
                    }
                  }}
                  placeholder="user@company.com"
                  readOnly={mode === 'edit'}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                    mode === 'edit' ? 'bg-default-100 cursor-not-allowed' : ''
                  } ${
                    emailError 
                      ? 'border-danger focus:ring-danger' 
                      : 'border-default-300 focus:ring-primary'
                  }`}
                />
                {emailError && (
                  <p className="mt-1 text-sm text-danger">{emailError}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="organizationType" className="block text-sm font-medium text-default-600 mb-1">
                    Organization Type
                  </label>
                  <Select
                    id="organizationType"
                    aria-label="Organization Type"
                    selectedKeys={formData.organizationType ? new Set([formData.organizationType]) : new Set([])}
                    size="md"
                    radius="md"
                    classNames={{ trigger: 'border border-default-200 bg-content1' }}
                    placeholder="Select organization type"
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys as Set<React.Key>)[0];
                      setFormData(prev => ({ ...prev, organizationType: selectedKey ? String(selectedKey) : '' }));
                    }}
                  >
                    {organizationTypeOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </Select>
                </div>
                <div>
                  <label htmlFor="organizationCode" className="block text-sm font-medium text-default-600 mb-1">
                    Organization Code
                  </label>
                  <input
                    id="organizationCode"
                    type="text"
                    value={formData.organizationCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, organizationCode: e.target.value }))}
                    placeholder="Enter organization code"
                    className="w-full px-3 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="organizationName" className="block text-sm font-medium text-default-600 mb-1">
                  Organization Name
                </label>
                <input
                  id="organizationName"
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizationName: e.target.value }))}
                  placeholder="Enter organization name"
                  className="w-full px-3 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Assign Groups Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-foreground">Assign Roles</h3>
                <p className="text-sm text-default-500">Select which roles this user should be assigned to</p>
              </div>

              {selectedGroups.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-default-600">Selected Roles ({selectedGroups.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedGroups.map(groupId => (
                      <span key={groupId} className="inline-flex items-center gap-1 px-3 py-1 bg-default-100 text-default-700 rounded-full text-sm">
                        {allGroups.find((g: any) => g.group === groupId)?.group || groupId}
                        <button onClick={() => handleRemoveGroup(groupId)} className="ml-1 hover:bg-default-200 rounded-full p-0.5">
                          <Icon icon="mdi:close" className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Groups Dropdown */}
              <div className="space-y-2">
                <button
                  onClick={() => setIsGroupsExpanded(!isGroupsExpanded)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-default-300 rounded-md hover:bg-default-100"
                >
                  <span className="text-default-500">
                    {selectedGroups.length > 0 ? `${selectedGroups.length} Roles selected` : 'Select Roles...'}
                  </span>
                  <Icon 
                    icon={isGroupsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} 
                    className="w-4 h-4 text-default-400" 
                  />
                </button>

                {isGroupsExpanded && (
                  <div className="border border-default-200 rounded-md p-3 space-y-3">
                    {/* Search Bar */}
                    <div className="relative">
                      <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-default-400" />
                      <input
                        type="text"
                        placeholder="Search Roles..."
                        value={groupSearchTerm}
                        onChange={(e) => setGroupSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    {/* Groups List */}
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {filteredGroups.map((group: any) => (
                        <label
                          key={group.group}
                          className="flex items-center space-x-3 p-2 hover:bg-default-100 rounded cursor-pointer"
                          htmlFor={`group-${group.group}`}
                          aria-label={`Select group ${group.group}`}
                        >
                          <input
                            id={`group-${group.group}`}
                            type="checkbox"
                            checked={selectedGroups.includes(group.group)}
                            onChange={() => handleGroupToggle(group.group)}
                            className="w-4 h-4 text-primary border-default-300 rounded focus:ring-primary"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-foreground">{group.group}</div>
                            <div className="text-sm text-default-500">
                              {(group.entity_resources?.length || 0)} resources • {group.member_count || 0} users
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* User Attributes Section */}
            <UserAttributesEditor
              attributes={userAttributes}
              onChange={setUserAttributes}
            />
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-default-200 flex justify-end gap-3">
            <CtyButton
              variant="bordered"
              onPress={() => onOpenChange(false)}
              isDisabled={isLoading}
            >
              Cancel
            </CtyButton>
            <CtyButton
              color="primary"
              onPress={handleSubmit}
              isDisabled={!isFormValid || isLoading}
              isLoading={isLoading}
            >
              {mode === 'create' ? 'Create User' : 'Save Changes'}
            </CtyButton>
          </div>
          </>
          )}
        </div>
      </div>
    </>
  );
}

export default CreateEditUserModal;
